// §26.5 — Chat: 1:1, connections only, text only.
//
// **The one rule this app exists to keep is that a message is text.** Every body below is rendered
// as a React child, which is a text node — never `dangerouslySetInnerHTML`, never a markdown pass,
// never a link scanner. §26.9 states it as a security rule and it is the right one: the bodies
// arrive from another person, and the only reason `<img src=x onerror=…>` is harmless here is that
// nothing on this path ever asks a browser to parse it. A unit test posts exactly that string and
// asserts it comes out as characters (M29.4).
//
// It follows that there are **no link previews** either. §26.9 is explicit — a preview fetcher on a
// public host is an SSRF surface, and D-015 already killed the content proxy for the same reason.
//
// Everything the app can do arrives as a `ChatShellAccess` (D-059): it reaches no kernel service
// and holds no `fetch` (§6.5). The doorbell it subscribes to is the shell's, so a new message
// re-`GET`s through the same code path a cold load uses — there is one way to get a thread onto the
// screen, which is why "works over SSE, stale on the poll" is not a state that can exist.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatShellAccess, Message, Thread } from '@hangar/shared';
import { CHAT_PAGE_SIZE, MAX_MESSAGE_CHARS } from '@hangar/shared';
import { COPY } from '@/copy';
import './chat.css';

export interface ChatProps {
  access?: ChatShellAccess;
  /** Which conversation to open on mount. Absent means the busiest one (§26.5's thread list order). */
  initialPeer?: string;
}

/** `HH:MM`, in the reader's own locale. A date is not shown: a thread is read as a stream. */
const clock = (at: number): string =>
  new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** What a thread row shows under the name — the last thing said, or the tombstone (D-218). */
const preview = (last: Message | null): string => {
  if (last === null) return '';
  return last.deleted ? COPY.chat.deleted : (last.body ?? '');
};

export function Chat({ access, initialPeer }: ChatProps) {
  const [threads, setThreads] = useState<readonly Thread[]>([]);
  // D-345 — a notification click asked for a conversation. Read at mount, because the click is
  // what opened this window and the request was made before it existed.
  const [peer, setPeer] = useState<string | null>(
    () => initialPeer ?? access?.pendingThread() ?? null,
  );
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  /** True while the last page came back full — so there may be more behind it. */
  const [more, setMore] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);
  const me = access?.me() ?? null;

  const loadThreads = useCallback(async () => {
    if (access === undefined) return;
    const rows = await access.threads().catch(() => [] as Thread[]);
    setThreads(rows);
    // Open something rather than nothing: a two-pane app whose right half is empty on every boot
    // makes the user click before it has told them anything.
    setPeer((current) => current ?? rows[0]?.peer ?? null);
  }, [access]);

  const loadMessages = useCallback(
    async (who: string | null) => {
      if (access === undefined || who === null) {
        setMessages([]);
        setMore(false);
        return;
      }
      const page = await access.messages(who).catch(() => [] as Message[]);
      setMessages(page);
      setMore(page.length >= CHAT_PAGE_SIZE);
    },
    [access],
  );

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    void loadMessages(peer);
    // The search is over *this* conversation's loaded history, so it does not survive a switch —
    // a filter still applied to a thread you have moved away from reads as an empty conversation.
    setTerm('');
  }, [loadMessages, peer]);

  // D-345 — and the other half: a click while this window is already open has no mount to read
  // the request at, so the shell says so directly.
  useEffect(() => {
    if (access === undefined) return;
    return access.onOpenThread((requested) => setPeer(requested));
  }, [access]);

  // §26.6 — the doorbell says *that* something changed; both halves re-`GET`. The unread counts
  // live on the thread list, so a message arriving in a thread nobody is looking at still shows.
  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe(() => {
      void loadThreads();
      void loadMessages(peer);
    });
  }, [access, loadThreads, loadMessages, peer]);

  // Newest at the bottom, so the pane belongs at the bottom whenever its contents change.
  useEffect(() => {
    const log = logRef.current;
    if (log !== null) log.scrollTop = log.scrollHeight;
  }, [messages]);

  const earlier = useCallback(async () => {
    if (access === undefined || peer === null) return;
    const oldest = messages[0];
    if (oldest === undefined) return;
    const page = await access.messages(peer, { before: oldest.id }).catch(() => [] as Message[]);
    setMore(page.length >= CHAT_PAGE_SIZE);
    if (page.length === 0) return;
    // Prepended, and the scroll position is left where it was: the reader asked for what came
    // before, not to be moved somewhere else.
    setMessages((current) => [...page, ...current]);
  }, [access, peer, messages]);

  const send = useCallback(async () => {
    if (access === undefined || peer === null) return;
    const body = draft.trim();
    if (body.length === 0) return;
    setNotice(null);
    try {
      const message = await access.send(peer, body);
      // Cleared only after the server took it — a box that empties on a failed send loses what
      // somebody typed, which is worse than the failure.
      setDraft('');
      setMessages((current) => [...current, message]);
      void loadThreads();
    } catch (error: unknown) {
      // §16's three refusals each mean something different to the person typing, and "that didn't
      // send" is the honest fallback for anything else.
      const status = (error as { status?: number } | null)?.status;
      setNotice(
        status === 413
          ? COPY.chat.tooLong
          : status === 403
            ? COPY.chat.notConnected
            : status === 429
              ? COPY.chat.tooFast
              : COPY.chat.sendFailed,
      );
    }
  }, [access, peer, draft, loadThreads]);

  const remove = useCallback(
    async (message: Message) => {
      if (access === undefined) return;
      // §19.4 — through the shell, and the message says what actually happens: it goes for both
      // of you (D-218), which "are you sure?" does not convey.
      if (!(await access.confirm(COPY.chat.confirmDelete))) return;
      await access.remove(message.id).catch(() => {});
      await loadMessages(peer);
      void loadThreads();
    },
    [access, peer, loadMessages, loadThreads],
  );

  /**
   * §26.5 — "a client-side search over loaded history".
   *
   * Client-side is the whole specification: it filters what is on screen and does not fetch. That
   * is why `searchEmpty` says "nothing *loaded* here matches" — telling somebody there are no
   * results when the answer is three pages back would be a lie the app is in a position to know.
   */
  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (needle.length === 0) return messages;
    return messages.filter((message) => (message.body ?? '').toLowerCase().includes(needle));
  }, [messages, term]);

  const current = threads.find((thread) => thread.peer === peer) ?? null;

  return (
    <div className="chat" data-testid="chat">
      <aside className="chat-threads">
        <h2 className="chat-threads-title">{COPY.chat.threadsTitle}</h2>
        {threads.length === 0 ? (
          <p className="chat-empty" data-testid="chat-no-threads">
            {COPY.chat.noThreads}
          </p>
        ) : (
          <ul className="chat-thread-list">
            {threads.map((thread) => (
              <li key={thread.peer}>
                <button
                  type="button"
                  className="chat-thread"
                  data-testid={`chat-thread-${thread.peer}`}
                  aria-current={thread.peer === peer}
                  onClick={() => setPeer(thread.peer)}
                >
                  <span className="chat-thread-top">
                    <span className="chat-thread-name">{thread.displayName}</span>
                    {thread.unread > 0 ? (
                      <span className="chat-unread" data-testid={`chat-unread-${thread.peer}`}>
                        {COPY.chat.unread(thread.unread)}
                      </span>
                    ) : null}
                  </span>
                  <span className="chat-thread-preview">{preview(thread.last)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="chat-pane">
        {peer === null ? (
          <p className="chat-empty" data-testid="chat-no-thread">
            {COPY.chat.noThread}
          </p>
        ) : (
          <>
            <header className="chat-head">
              <h1 className="chat-peer" data-testid="chat-peer">
                {current?.displayName ?? peer}
              </h1>
              <input
                className="chat-search"
                data-testid="chat-search"
                type="search"
                aria-label={COPY.chat.searchLabel}
                placeholder={COPY.chat.searchLabel}
                value={term}
                onChange={(event) => setTerm(event.currentTarget.value)}
              />
            </header>

            <div className="chat-log" data-testid="chat-log" ref={logRef}>
              {more && term.trim().length === 0 ? (
                <button
                  type="button"
                  className="chat-action"
                  data-testid="chat-earlier"
                  onClick={() => void earlier()}
                >
                  {COPY.chat.earlier}
                </button>
              ) : null}

              {shown.length === 0 ? (
                <p className="chat-empty" data-testid="chat-empty">
                  {term.trim().length === 0
                    ? COPY.chat.noMessages
                    : COPY.chat.searchEmpty(term.trim())}
                </p>
              ) : null}

              {shown.map((message) => (
                <article
                  className="chat-message"
                  key={message.id}
                  data-mine={message.from === me}
                  data-testid={`chat-message-${message.id}`}
                >
                  <div className="chat-message-top">
                    <span className="chat-when">{clock(message.sentAt)}</span>
                    {message.deleted ? null : (
                      <button
                        type="button"
                        className="chat-delete"
                        data-testid={`chat-delete-${message.id}`}
                        onClick={() => void remove(message)}
                      >
                        {COPY.chat.delete}
                      </button>
                    )}
                  </div>
                  {/*
                    The body. A React child, so it is a text node — this is the line §26.9's "no
                    HTML, no markdown, no link previews" comes down to, and the whole reason the
                    XSS fixture passes.
                  */}
                  <p className="chat-body" data-deleted={message.deleted}>
                    {message.deleted ? COPY.chat.deleted : message.body}
                  </p>
                </article>
              ))}
            </div>

            {notice === null ? null : (
              <p className="chat-note" role="status" data-testid="chat-notice">
                {notice}
              </p>
            )}

            <div className="chat-send">
              <textarea
                className="chat-input"
                data-testid="chat-input"
                rows={2}
                maxLength={MAX_MESSAGE_CHARS}
                placeholder={COPY.chat.placeholder}
                value={draft}
                onChange={(event) => setDraft(event.currentTarget.value)}
                onKeyDown={(event) => {
                  // Enter sends, Shift+Enter breaks the line. A message is text and text has
                  // paragraphs; a chat box that cannot make one is a chat box people fight.
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void send();
                  }
                }}
              />
              <button
                type="button"
                className="chat-action"
                data-testid="chat-send"
                onClick={() => void send()}
              >
                {COPY.chat.send}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default Chat;
